// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedWorkflowSelectionProperty } from '@/audit/api/types';
import { AuditPropertyValue } from '@/audit/components/audit-property-value';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Box, Typography } from '@mui/material';

interface Props {
  /**
   * The workflow selection property to render.
   */
  property: CachedWorkflowSelectionProperty;
}
interface PropertyRowProps {
  label: string;
  oldValue: string | null;
  newValue: string | null;
  showArrow: boolean;
}

/**
 * Renders a single property row with label and value(s)
 */
function PropertyRow(props: PropertyRowProps) {
  return (
    <Typography
      display="flex"
      variant="caption"
      alignItems="center"
      sx={{ flexWrap: 'wrap' }}
    >
      <Typography
        component="span"
        variant="inherit"
        fontWeight="bold"
        sx={{ flexShrink: 0 }}
      >
        {props.label}:&nbsp;
      </Typography>
      {props.showArrow && (
        <>
          <AuditPropertyValue value={props.oldValue} />{' '}
          <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 0.5 }} />
        </>
      )}
      <AuditPropertyValue value={props.newValue} />
    </Typography>
  );
}

/**
 * A component that renders a workflow selection change as two separate properties:
 * - Workflow: workflow name (hidden if unchanged)
 * - Stage: stage name (always shown if changed)
 */
export function AuditWorkflowSelectionLabel(props: Props) {
  const hasOldValue = props.property.oldValue !== undefined;
  const showArrow = hasOldValue;

  const oldWorkflow = props.property.oldValue?.workflow.name ?? null;
  const newWorkflow = props.property.newValue?.workflow.name ?? null;

  const oldStage = props.property.oldValue?.name ?? null;
  const newStage = props.property.newValue?.name ?? null;

  // Show workflow row only if workflow actually changed
  const showWorkflowRow = !hasOldValue || oldWorkflow !== newWorkflow;

  return (
    <Box>
      {showWorkflowRow && (
        <PropertyRow
          label="Project workflow"
          oldValue={oldWorkflow}
          newValue={newWorkflow}
          showArrow={showArrow}
        />
      )}
      <PropertyRow
        label="Stage"
        oldValue={oldStage}
        newValue={newStage}
        showArrow={showArrow}
      />
    </Box>
  );
}
